import { EditOutlined } from '@ant-design/icons';
import { Card, Row, Tag, theme } from 'antd';
import React, { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ENV } from 'src/';
import { AutocompleteTextFilter } from 'src/common/components/table-filters/autocomplete-text-filter';
import { emptyTableModel } from 'src/common/utils';
import { AppConfiguration } from 'src/core/services/configuration.service';
import { formatMessage } from 'src/core/services/http.service';
import { Query } from 'src/core/stores/data-store';
import { TableModel, TableView } from 'src/core/ui/collections/table';
import { setHeader, setRoutes } from 'src/core/ui/shell';
import { AppConfig, container } from 'src/inversify.config';
import { AccessTokenType, ClientItem, ClientSummary, ClientSummaryStore, GrantType, StandardScopes, TokenUsage } from 'src/stores/clients-store';
import { ClientDetailsPage } from './client-details';
const { useToken } = theme;

const ClientsListPage: FC = () => {
  const { t } = useTranslation();
  const config: AppConfiguration = useContext(AppConfig);

  const [query, setQuery] = useState({
    searchQuery: '',
    orderBy: [{ field: 'clientName', direction: 'Ascending', useProfile: false }],
    skip: 0,
    take: 5,
  } as Query);

  const clientsStore = container.get(ClientSummaryStore);
  const clientsState = clientsStore.state;
  const [clientDetails, setClientDetails] = useState<{ mode: 'edit' | 'new'; item?: ClientItem } | undefined>();
  const { token } = useToken();

  setHeader(t('Clients'));
  setRoutes([
    { path: '', breadcrumbName: t('Home') },
    { path: 'clients', breadcrumbName: t('Clients') },
  ]);

  useEffect(
    () => {
      clientsStore.load(query);
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const tableModel = {
    query: query,
    columns: [
      {
        sortable: true,
        searchable: true,
        field: 'clientId',
        title: t('ClientID'),
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={clientsStore.baseUrl} fieldName={'ClientId'} fieldValue={'clientId'} />,
        fiterField: 'ClientId',
        renderer: (data) => <span>{data.clientId}</span>,
      },
      {
        sortable: true,
        searchable: true,
        field: 'clientName',
        title: t('ClientName'),
        applyPartialFilter: true,
        filter: <AutocompleteTextFilter baseUrl={clientsStore.baseUrl} fieldName={'ClientName'} fieldValue={'clientName'} />,
        fiterField: 'ClientName',
        renderer: (data) => <span>{data.clientName}</span>,
      },
      {
        sortable: true,
        searchable: false,
        field: 'protocolType',
        title: t('ProtocolType'),
        renderer: (data) => <span>{data.protocolType}</span>,
      },
      {
        sortable: false,
        searchable: false,
        field: 'allowedGrantTypes',
        title: t('AllowedGrantTypes'),
        renderer: (data) =>
          data.allowedGrantTypes.map((grant) => (
            <Tag style={{ marginBottom: 8 }}>
              {t(
                Object.entries(GrantType)
                  .find((v) => v[1] === grant)
                  ?.firstOrDefault() ?? grant
              )}
            </Tag>
          )),
      },
      {
        sortable: false,
        searchable: false,
        field: 'allowedScopes',
        title: t('AllowedScopes'),
        renderer: (data) =>
          data.allowedScopes.map((scope) => (
            <Tag style={{ marginBottom: 8 }}>
              {t(
                Object.entries(StandardScopes)
                  .find((v) => v[1] === scope)
                  ?.firstOrDefault() ?? scope
              )}
            </Tag>
          )),
      },
      {
        sortable: false,
        searchable: false,
        field: 'redirectUris',
        title: t('RedirectUris'),
        renderer: (data) => data.redirectUris.map((uri) => <Tag style={{ marginBottom: 8 }}>{uri}</Tag>),
      },
      {
        sortable: false,
        searchable: false,
        field: 'postLogoutRedirectUris',
        title: t('PostLogoutRedirectUris'),
        renderer: (data) => data.postLogoutRedirectUris.map((uri) => <Tag style={{ marginBottom: 8 }}>{uri}</Tag>),
      },
      {
        sortable: true,
        searchable: false,
        field: 'accessTokenType',
        title: t('AccessTokenType'),
        renderer: (data) => (
          <Tag style={{ marginRight: 60 }}>
            {t(
              (Object.entries(AccessTokenType)
                .find((v) => v[1] === data.accessTokenType)
                ?.firstOrDefault() ?? '') as string
            )}
          </Tag>
        ),
      },
      {
        sortable: true,
        searchable: false,
        field: 'refreshTokenUsage',
        title: t('RefreshTokenUsage'),
        renderer: (data) => (
          <Tag>
            {t(
              (Object.entries(TokenUsage)
                .find((v) => v[1] === data.refreshTokenUsage)
                ?.firstOrDefault() ?? '') as string
            )}
          </Tag>
        ),
      },
      {
        sortable: true,
        searchable: false,
        field: 'accessTokenLifetime',
        title: t('AccessTokenLifetime'),
        renderer: (data) => <span style={{ marginRight: 60 }}>{data.accessTokenLifetime}</span>,
      },
      {
        sortable: true,
        searchable: false,
        field: 'absoluteRefreshTokenLifetime',
        title: t('AbsoluteRefreshTokenLifetime'),
        renderer: (data) => <span style={{ marginRight: 60 }}>{data.absoluteRefreshTokenLifetime}</span>,
      },
      {
        sortable: true,
        searchable: false,
        field: 'allowAccessTokensViaBrowser',
        title: t('AllowAccessTokensViaBrowser'),
        renderer: (data) =>
          data.allowAccessTokensViaBrowser ? (
            <span style={{ color: token.colorSuccess }}>{t('Yes')}</span>
          ) : (
            <span style={{ color: token.colorError }}>{t('No')}</span>
          ),
      },
      {
        sortable: true,
        searchable: false,
        field: 'allowOfflineAccess',
        title: t('AllowOfflineAccess'),
        renderer: (data) =>
          data.allowOfflineAccess ? (
            <span style={{ color: token.colorSuccess }}>{t('Yes')}</span>
          ) : (
            <span style={{ color: token.colorError }}>{t('No')}</span>
          ),
      },
      {
        sortable: true,
        searchable: false,
        field: 'alwaysIncludeUserClaimsInIdToken',
        title: t('AlwaysIncludeUserClaimsInIdToken'),
        renderer: (data) =>
          data.alwaysIncludeUserClaimsInIdToken ? (
            <span style={{ color: token.colorSuccess }}>{t('Yes')}</span>
          ) : (
            <span style={{ color: token.colorError }}>{t('No')}</span>
          ),
      },
      {
        sortable: true,
        searchable: false,
        field: 'requireClientSecret',
        title: t('RequireClientSecret'),
        renderer: (data) =>
          data.requireClientSecret ? (
            <span style={{ color: token.colorSuccess }}>{t('Yes')}</span>
          ) : (
            <span style={{ color: token.colorError }}>{t('No')}</span>
          ),
      },
      {
        sortable: true,
        searchable: false,
        field: 'requireConsent',
        title: t('RequireConsent'),
        renderer: (data) =>
          data.requireConsent ? (
            <span style={{ color: token.colorSuccess }}>{t('Yes')}</span>
          ) : (
            <span style={{ color: token.colorError }}>{t('No')}</span>
          ),
      },
      {
        sortable: true,
        searchable: false,
        field: 'requirePkce',
        title: t('RequirePkce'),
        renderer: (data) =>
          data.requirePkce ? (
            <span style={{ color: token.colorSuccess }}>{t('Yes')}</span>
          ) : (
            <span style={{ color: token.colorError }}>{t('No')}</span>
          ),
      },
      {
        fixed: 'right',
        sortable: false,
        searchable: false,
        field: 'edit',
        title: t('Edit'),
        renderer: (data) => {
          let itemData = data as ClientItem & { key?: number };
          delete itemData.key;
          return (
            <span className="clickable-span strong" onClick={() => setClientDetails(() => ({ mode: 'edit', item: itemData }))}>
              {<EditOutlined />}
            </span>
          );
        },
      },
    ],
    data: clientsState.value,
  } as TableModel<ClientSummary>;

  return (
    <>
      <Row align="top" justify="space-between" className="table-overflow">
        <Card className="card-table" style={{ width: '100%' }}>
          <TableView
            pageSize={5}
            pageSizeOptions={['5', '10', '15']}
            rowKey="id"
            canCreateNew={true}
            onNewItem={() => {
              setClientDetails(() => ({ mode: 'new' }));
            }}
            newItemName={t('Add')}
            canDelete={false}
            canEdit={false}
            canSort
            onQueryChanged={(query: Query) => {
              setQuery(query);
              clientsStore.load(query);
            }}
            onRefresh={() => clientsStore.load(query)}
            model={clientsState.errorMessage.value ? emptyTableModel : tableModel}
            error={
              clientsState.errorMessage.value &&
              (config.environment === ENV.DEV ? formatMessage(clientsState.errorMessage.value) : t('ClientsTableError'))
            }
          />
        </Card>
      </Row>

      {clientDetails !== undefined && (
        <ClientDetailsPage
          mode={clientDetails.mode}
          clientItem={clientDetails.item}
          onClose={(reload) => {
            setClientDetails(() => undefined);
            if (reload) clientsStore.load(query);
          }}
        />
      )}
    </>
  );
};

export default ClientsListPage;
